import React, { useCallback, useMemo, useState } from "react";
import { extractFirstQueryParam } from "domains/commons/misc";
import ExploreList from "domains/explore/components/ExploreList";
import { ExploreTabId } from "domains/explore/constants";
import { FmFileImage } from "domains/file-manager/interfacesV2";
import { useCreateNewModelModalContext } from "domains/models/contexts/CreateNewModelModalProvider";
import useRouter from "domains/navigation/hooks/useRouter";
import Button, { ButtonProps } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import { Box, HStack, Text, VStack } from "@chakra-ui/react";

import { Section, SectionAction } from "../SectionBase";

export default function SectionExplore() {
  const router = useRouter();
  const { isUnsigned, openSignUpModal } = useUserContext();
  const { open: openModalCreateModel } = useCreateNewModelModalContext();
  const [tabId, setTabId] = useState<ExploreTabId>("image");
  const initialIndex = extractFirstQueryParam(router.query.initialIndex);

  const mainCtaProps = useMemo<ButtonProps>(() => {
    switch (tabId) {
      case "image":
        return { internalLink: "/images/new", children: "Generate" };
      case "3d":
        return { internalLink: "/3d/new", children: "Generate" };
      case "canvas":
        return { internalLink: "/canvas/new", children: "Generate" };
      case "model":
        return {
          onClick: () => {
            if (isUnsigned) {
              openSignUpModal("generate");
            } else {
              openModalCreateModel();
            }
          },
          children: "Train",
        };
      case "skybox":
        return { internalLink: "/skyboxes/new", children: "Generate" };
      case "texture":
        return { internalLink: "/textures/new", children: "Generate" };
      case "video":
        return { internalLink: "/videos/new", children: "Generate" };
    }
  }, [isUnsigned, openModalCreateModel, openSignUpModal, tabId]);

  const handleCardClick = useCallback(
    async (file: FmFileImage, index: number) => {
      // Track the first click on the Home page
      Track(AnalyticsEvents.Explore.ClickedAsset, {
        level: 1,
        assetId: file.id,
        assetKind: file.meta.metadata.kind,
        assetType: file.meta.metadata.type,
        assetSource: file.meta.source,
        assetModel: file.meta.metadata.modelId,
        assetModelType: file.meta.metadata.modelType,
      });

      if (isUnsigned) {
        openSignUpModal("generate");
        return;
      }

      await router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            initialIndex: String(index),
          },
        },
        "",
        { shallow: true }
      );
      await router.push({
        pathname: "/explore/asset/[id]",
        query: {
          id: file.id,
        },
      });
    },
    [isUnsigned, openSignUpModal, router]
  );

  const handleCtaClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, trackingEvent?: string) => {
      if (trackingEvent) {
        Track(trackingEvent, {
          persona: "default",
          userIsAuthenticated: !isUnsigned,
          context: "home",
        });
      }

      if (isUnsigned) {
        e.preventDefault();
        openSignUpModal("generate");
      }
    },
    [isUnsigned, openSignUpModal]
  );

  const handleViewAllClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      Track(AnalyticsEvents.Explore.ClickedViewAll, {
        persona: "default",
        userIsAuthenticated: !isUnsigned,
        context: "home",
      });

      if (isUnsigned) {
        e.preventDefault();
        openSignUpModal("generate");
      }
    },
    [isUnsigned, openSignUpModal]
  );

  // ----------------------------------

  return (
    <Section
      title="Explore"
      action={
        <SectionAction
          internalLink={{
            pathname: "/explore",
            query: {
              tab: tabId,
            },
          }}
          rightIcon={<Icon id="Ui/ChevronRight" />}
          onClick={handleViewAllClick}
        >
          View All
        </SectionAction>
      }
    >
      <ExploreList
        context="home"
        tabId={tabId}
        onTabChange={(tabId) => setTabId(tabId)}
        onCardClick={handleCardClick}
        initialIndex={initialIndex ? parseInt(initialIndex) : undefined}
      />

      <Box pos="relative" zIndex={1} mt="-400px">
        <Box
          h="200px"
          mt="-1px"
          bg="linear-gradient(180deg, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 1) 74.61%)"
        />

        <VStack bgColor="background.500" spacing={4}>
          <Text textAlign="center" size="title.md">
            Create or explore.
            <br />
            One click to your next assets.
          </Text>

          <HStack spacing={3}>
            <Button
              size="sm"
              variant="secondary"
              onClick={(e) =>
                handleCtaClick(e, AnalyticsEvents.Explore.ClickedGenerateNow)
              }
              {...mainCtaProps}
            />

            <Button
              size="sm"
              variant="primary"
              colorScheme="white"
              internalLink="/explore"
              onClick={(e) =>
                handleCtaClick(e, AnalyticsEvents.Explore.ClickedOpenExplore)
              }
            >
              Explore
            </Button>
          </HStack>
        </VStack>

        <Box h="100px" mt="-1px" bgColor="background.500" />
      </Box>
    </Section>
  );
}
